import type { AnchorHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "secondary" | "onDark" | "outlineOnDark" | "quiet";
type Size = "md" | "lg";

const BASE = [
  "group/cta inline-flex items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap",
  "cursor-pointer select-none",
  // Survol en 150 ms, pression rapide (70 ms), propriétés animées listées.
  "transition-[background-color,color,border-color,box-shadow,translate,scale,text-decoration-color] duration-150 ease-(--ease-standard)",
  "active:scale-[0.97] active:duration-[70ms] motion-reduce:active:scale-100",
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
].join(" ");

const SIZES: Record<Size, string> = {
  md: "h-11 px-5 text-sm",
  lg: "h-12 px-6 text-[15px]",
};

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-action text-action-foreground shadow-raised hover:bg-action-strong hover:-translate-y-px motion-reduce:hover:translate-y-0",
  secondary:
    "border border-border-strong bg-surface text-foreground hover:border-input hover:bg-muted",
  onDark:
    "bg-surface text-primary hover:bg-info-soft focus-visible:outline-primary-foreground",
  outlineOnDark:
    "border border-primary-foreground/35 text-primary-foreground hover:bg-primary-foreground/10 focus-visible:outline-primary-foreground",
  quiet:
    "px-0 text-action underline decoration-action/35 underline-offset-4 hover:decoration-action active:scale-100",
};

const EXTERNAL = /^(mailto:|tel:|https?:)/;

/**
 * Lien en forme de bouton pour le site public (règles du rulebook UI :
 * curseur, cible de 44 px et plus, focus visible, pression asymétrique).
 * `to` interne → `Link` du routeur ; email ou URL externe → `<a>`.
 */
export function CtaLink({
  to,
  variant = "primary",
  size = "lg",
  arrow = false,
  className,
  children,
  ...rest
}: {
  to: string;
  variant?: Variant;
  size?: Size;
  arrow?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className">) {
  const classes = cn(
    BASE,
    variant === "quiet" ? "h-auto text-[15px]" : SIZES[size],
    VARIANTS[variant],
    className,
  );
  const content = (
    <>
      {children}
      {arrow && (
        <ArrowRight
          aria-hidden
          className="size-4 transition-transform duration-150 group-hover/cta:translate-x-0.5 motion-reduce:transform-none"
        />
      )}
    </>
  );
  if (EXTERNAL.test(to)) {
    return (
      <a href={to} className={classes} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} className={classes} {...rest}>
      {content}
    </Link>
  );
}
